import React, { useEffect, useState } from 'react';
import { X, Info, AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';

export function AnnouncementPopup() {
  const api = useApi();
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    loadUnreadAnnouncements();
    // Poll every 60 seconds
    const interval = setInterval(loadUnreadAnnouncements, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const loadUnreadAnnouncements = async () => {
    try {
      const data = await api.get('/announcements/unread');
      setAnnouncements(data.filter(a => !dismissed.has(a.id)));
    } catch (error) {
      console.error('Failed to load announcements:', error);
    }
  };

  const handleDismiss = async (announcementId, isDismissible) => {
    if (!isDismissible) return;
    
    try {
      // Mark as read
      await api.post(`/announcements/${announcementId}/mark-read`);
      
      // Remove from local state
      setDismissed(prev => new Set(prev).add(announcementId));
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
    } catch (error) {
      console.error('Failed to dismiss announcement:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'urgent':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-blue-600" />;
    }
  };

  const getStyles = (type) => {
    switch (type) {
      case 'urgent':
        return 'bg-red-50 border-red-300 border-l-4 border-l-red-500';
      case 'warning':
        return 'bg-yellow-50 border-yellow-300 border-l-4 border-l-yellow-500';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-300 border-l-4 border-l-blue-500';
    }
  };

  if (announcements.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 space-y-3 max-w-sm pointer-events-none">
      {announcements.map((announcement) => (
        <div
          key={announcement.id}
          className={`${getStyles(announcement.type)} border rounded-lg shadow-lg p-4 animate-slide-in pointer-events-auto`}
        >
          <div className="flex items-start gap-3">
            {getIcon(announcement.type)}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm mb-1">{announcement.title}</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{announcement.message}</p>
              {announcement.created_by_name && (
                <p className="text-xs text-gray-500 mt-2">
                  From: {announcement.created_by_name}
                </p>
              )}
            </div>
            {announcement.is_dismissible && (
              <button
                onClick={() => handleDismiss(announcement.id, announcement.is_dismissible)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
